<template>
  <div class="mb-5">
    <div class="mb-2" style="color: #9E9E9E; font-weight: 400; font-style: italic; letter-spacing: 0.05em; text-align: center;" v-if="!readonly">
      {{ `You Are Viewing The "${scenario.name} Scenario"` }}
    </div>
    <v-layout row wrap>
      <v-flex xs12 sm12 md5 class="mt-3" style="padding: 0 10px;">
        <div class="group-name">Filter by race, state, or candidate</div>
        <v-text-field
          class="ie-search-bar"
          label="ex. Virginia, va 10, or Wexton"
          single-line
          hide-details
          prepend-icon="search"
          :value="search"
          @input="searchFiltering"
          light
        ></v-text-field>
      </v-flex>
      <v-flex xs12 sm12 md5 class="mt-2" style="padding: 0 10px;">
        <tag-selector :tags.sync="tags" @tag-update="tags = $event"></tag-selector>
      </v-flex>
      <v-flex xs12 sm12 md2 class="mt-2" style="padding: 0 10px; text-align: center;">
        <div class="group-name">Filter races by all or edited</div>
        <v-btn-toggle v-model="rowSelection">
          <v-btn small flat value="all">
            All
          </v-btn>
          <v-btn small flat value="edited">
            Only Edited
          </v-btn>
        </v-btn-toggle>
      </v-flex>
    </v-layout>

    <v-data-table
      ref="dataTable"
      id="expenditures-table"
      class="fixed-header"
      :style="`height: ${height}; overflow: auto;`"
      :headers="mobile ? filteredHeaders.filter(e => e.text !== 'Prediction') : filteredHeaders"
      :items="filteredExpenditures"
      :pagination.sync="pagination"
      item-key="race">
      <template slot="headers" slot-scope="props">
        <tr style="height: 34px; border-bottom: none;">
          <th :colspan="electionColSpan" class="group-col-header">Election</th>
          <th colspan="3" class="group-col-header" style="border-left: 1px solid rgba(0,0,0,0.12);">
            <template v-if="mode === 'dem'">Dem </template>
            <template v-else>GOP </template>
            Win Probability
          </th>
          <th colspan="4" class="group-col-header" style="border-left: 1px solid rgba(0,0,0,0.12);">Expenditures</th>
        </tr>
        <tr style="height: 38px; border-bottom: 1px solid rgba(0,0,0,0.12);">
          <th v-for="header in props.headers" :key="header.text"
            :class="['column', header.sortable ? 'sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
            :style="`text-align: ${header.align}; ${['GOP', 'Old'].includes(header.text) ? 'border-left: 1px solid rgba(0,0,0,0.12);' : ''};`"
            @click="changeSort(header.value)"
          >
            {{ header.text }}
            <v-icon v-if="header.sortable">arrow_upward</v-icon>
          </th>
        </tr>
      </template>
      <template slot="items" slot-scope="props">
        <tr class="ie-table-row">
          <td>
            {{ props.item.name }}
            <div v-if="mobile"
              class="race-state"
              :style="'margin-top: 5px; max-width: 80px; background-color: ' + props.item.prediction.color"
              v-html="getRaceState(props.item.prediction)">
            </div>
          </td>
          <td v-if="!mobile">
            <div class="race-state" :style="'max-width: 80px; background-color: ' + props.item.prediction.color" v-html="getRaceState(props.item.prediction)"></div>
          </td>
          <td :style="mobile ? 'display: flex; margin-top: 10px;' : 'display: flex; align-items: center;'" v-if="!readonly">
            <v-btn
              flat icon
              @click="props.expanded = !props.expanded"
              style="color: #57AEA6; margin: 3px 0; font-size: 0.9em;">
              <v-icon>info</v-icon>
            </v-btn>
            <v-btn
              flat icon
              :disabled="rowChange(props.item)"
              @click="$emit('reset-row', props.item.race)"
              style="color: #F7966B; margin-left: 10px; font-size: 0.9em;">
              <v-icon>redo</v-icon>
            </v-btn>
          </td>

          <td style="text-align: start; border-left: 1px solid rgba(0,0,0,0.12);">
            <template v-if="['god', 'gop'].includes(mode)">{{ props.item.originalProbability | votePercent(props.item.unopposedD, props.item.unopposedR) }}</template>
            <template v-else>{{ (1 - props.item.originalProbability) | votePercent(props.item.unopposedD, props.item.unopposedR) }}</template>
          </td>
          <td style="text-align: start;">
            <template v-if="['god', 'gop'].includes(mode)">{{ props.item.finalProbability | votePercent(props.item.unopposedD, props.item.unopposedR) }}</template>
            <template v-else>{{ (1 - props.item.finalProbability) | votePercent(props.item.unopposedD, props.item.unopposedR) }}</template>
          </td>
          <td :style="`text-align: start; border-right: 1px solid rgba(0,0,0,0.12); color: ${props.item.unopposedD === 1 || props.item.unopposedR === 1 ? '' : props.item.probabilityDelta.color};`">
            <template v-if="['god', 'gop'].includes(mode)">{{ props.item.probabilityDelta.value | votePercent(props.item.unopposedD, props.item.unopposedR, true) }}</template>
            <template v-else>{{ props.item.probabilityDelta.value | votePercent(props.item.unopposedD, props.item.unopposedR, true) }}</template>
          </td>

          <td :style="`cursor: pointer; text-align: start; ${editableFieldStyle('gop')}`"
            @click="['god', 'gop'].includes(mode) && !readonly ? editField(props.item.race, 'gopExpenditures', props.item['gopExpenditures'], $event.target) : ''">
            <span style="pointer-events: none">{{ props.item['gopExpenditures'] | money }}</span>
          </td>

          <td :style="`text-align: start; color: ${props.item.gopChange.color};`">
            <span v-if="props.item.gopChange.negative">-</span>
            {{ props.item.gopChange.absValue | money }}
          </td>

          <td :style="`cursor: pointer; text-align: start; ${editableFieldStyle('dem')}`"
            @click="['god', 'dem'].includes(mode) && !readonly ? editField(props.item.race, 'demExpenditures', props.item['demExpenditures'], $event.target) : ''">
            <span style="pointer-events: none">{{ props.item['demExpenditures'] | money }}</span>
          </td>

          <td :style="`text-align: start; color: ${props.item.demChange.color};`">
            <span v-if="props.item.demChange.negative">-</span>
            {{ props.item.demChange.absValue | money }}
          </td>
        </tr>
      </template>
      <template slot="expand" slot-scope="props" v-if="!readonly">
        <v-card flat>
          <v-card-text>
            <div style="margin: 0 10px;">
              <b>Candidates: </b>
              <span v-for="(candidate, index) in props.item.candidates" :key="index">
                {{ candidate.name }} ({{ candidate.party | initial }})
                <template v-if="(index + 1) < props.item.candidates.length">, </template>
              </span>
            </div>
            <a style="margin: 0 10px;" target="_blank" :href="`https://0ptimus.decisiondeskhq.com/house/${props.item.forecastingId}`">Forecast Model</a>
            <a style="margin: 0 10px;" target="_blank" :href="`https://results.decisiondeskhq.com/general/${props.item.resultsId === 'house_at-large' ? 'house_00' : props.item.resultsId}`">2018 Election Results</a>
          </v-card-text>
        </v-card>
      </template>
    </v-data-table>

    <v-card id="ie-table-footer" dark>
      <div style="padding: 10px; overflow-x: auto;">
        <div style="width: 100%; overflow-x: auto;">
          <scoreboard-mobile :totalSpend="totalSpend" :maxSpend="maxSpend" :fundsRemaining="fundsRemaining" :favored="favored" :expected="expected" v-if="mobilePhone"></scoreboard-mobile>
          <scoreboard :totalSpend="totalSpend" :maxSpend="maxSpend" :fundsRemaining="fundsRemaining" :favored="favored" :expected="expected" v-else></scoreboard>
        </div>
      </div>
      <div style="text-align: center; padding: 10px;" v-if="!mobile">
        <slot name="buttons"></slot>
      </div>
    </v-card>

    <v-card style="display: flex; justify-content: space-around; margin: 10px 0; padding: 10px;" v-if="mobile && !readonly">
      <slot name="buttons"></slot>
    </v-card>
  </div>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'

import calculator from '@/mixins/calculator'
import Scoreboard from './Scoreboard'
import ScoreboardMobile from './ScoreboardMobile'
import { TagSelector } from '@/components/common'

const name = 'expenditures'

const mixins = [
  calculator
]

const props = {
  expenditures: {
    type: Array,
    required: true
  },
  original: {
    type: Array,
    required: false
  },
  editableFields: {
    type: Array,
    required: true
  },
  scenario: {
    type: Object,
    required: false
  },
  readonly: {
    type: Boolean,
    required: false
  }
}

const computed = {
  ...mapGetters({
    mode: 'calculator/mode',
    mobile: 'ux/mobile',
    mobilePhone: 'ux/mobilePhone'
  }),
  filteredExpenditures () {
    let expenditures = this.expenditures
    if (this.rowSelection === 'edited') {
      expenditures = this.expenditures.filter(row => {
        const original = this.original.find(e => e.race === row.race)
        const userEdited = row.demExpenditures !== original.demExpenditures || row.gopExpenditures !== original.gopExpenditures
        const scenarioEdited = row.demChange.value !== 0 || row.gopChange.value
        return userEdited || scenarioEdited
      })
    }
    if (this.search) {
      this.tags = []
      return expenditures.filter((race) => {
        const search = this.search.trim().toLowerCase().split(/[ -]+/).filter((e) => e.trim().length > 0)
        const newObj = {}
        newObj.race = race.name
        newObj.state = newObj.race.slice(0, -3)
        newObj.stateAbbrev = race.forecastingId.substring(0, 2)
        newObj.cd = newObj.race.slice(-2)
        race.candidates.forEach(cand => { newObj[cand.party] = cand.name })
        const item = JSON.stringify(Object.values(newObj)).toLowerCase()
        return search.every(s => item.includes(s) === true)
      })
    }

    if (this.tags.length > 0) {
      return expenditures.filter((race) => {
        return race.prediction.state === this.tags[0]
      })
    }
    return expenditures
  },
  filteredHeaders () {
    if (this.readonly) {
      return this.headers.filter(item => item.value !== 'action')
    } else {
      return this.headers
    }
  },
  electionColSpan () {
    if (this.mobile && this.readonly) {
      return 1
    } else if (this.mobile || this.readonly) {
      return 2
    } else {
      return 3
    }
  }
}

const methods = {
  getNextField (race, field, current) {
    const items = this.$refs.dataTable.filteredItems
    let fieldIndex = this.editableFields.indexOf(field) + 1
    let raceIndex = items.findIndex(item => item.race === race)
    if (fieldIndex + 1 > this.editableFields.length) {
      raceIndex += 1
      fieldIndex = 0
    }
    if (raceIndex > items.length - 1) {
      raceIndex = 0
    }
    const nextRace = items[raceIndex]
    const nextField = this.editableFields[fieldIndex]
    const tbody = document.querySelector('.datatable tbody')
    const target = tbody.children[raceIndex * 2].children[(fieldIndex * 2) + 6]
    return { race: nextRace.race, field: nextField, value: nextRace[nextField], target }
  },
  editField (race, field, value, target) {
    const previous = document.getElementById('edit-input')
    if (previous) {
      previous.parentElement.removeChild(previous)
    }
    target.style.textIndent = '-9999px'

    const { top, left } = target.getBoundingClientRect()
    const edit = document.createElement('input')
    edit.target = target
    edit.id = 'edit-input'
    edit.type = 'number'
    edit.value = value
    edit.style.position = 'absolute'
    edit.style.top = `${top + window.scrollY}px`
    edit.style.left = `${left + window.scrollX}px`
    edit.style.padding = '0 10px'
    edit.style.outline = 'none'
    edit.style.width = '110px'
    edit.style.zIndex = '5001'

    const style = window.getComputedStyle(target)
    edit.style.setProperty('font-family', style.getPropertyValue('font-family'))
    edit.style.setProperty('font-size', style.getPropertyValue('font-size'))
    edit.style.height = style.getPropertyValue('height')

    document.body.appendChild(edit)
    edit.focus()
    edit.select()

    edit.onblur = (event) => {
      event.preventDefault()
      event.stopPropagation()
      let value = edit.value === '' ? 0 : parseFloat(edit.value)
      if (value > 1000000000) {
        value = 1000000000
      } else if (value < 0) {
        value = 0
      }
      this.$emit('update-row', { field, race, value: Math.round(value) })
      edit.parentElement.removeChild(edit)
      target.style.textIndent = ''
      return false
    }
    edit.addEventListener('keydown', (event) => {
      if (event.keyCode === 27) {
        edit.value = value
        edit.blur()
        return false
      } else if (event.keyCode === 13 || event.keyCode === 9) {
        event.preventDefault()
        event.stopPropagation()
        if (event.repeat === false) {
          target.textIndent = ''
          edit.blur()
          if (this.filteredExpenditures.length > 1) {
            const next = this.getNextField(race, field)
            this.editField(next.race, next.field, next.value, next.target)
            this.tableScroll()
          }
        }
      }
      return true
    })
  },
  changeSort (column) {
    if (this.pagination.sortBy === column) {
      this.pagination.descending = !this.pagination.descending
    } else {
      this.pagination.sortBy = column
      this.pagination.descending = false
    }
  },
  editableFieldStyle (party) {
    const color = party === 'gop' ? 'rgba(250,49,56,0.1)' : 'rgba(102,171,207, 0.1)'
    return ['god', party].includes(this.mode) ? `background-color: ${color}; color: #333; border: 2px solid #BDBDBD; border-top: none;` : ''
  },
  getRaceState (prediction) {
    return `<span style="color: ${prediction.textColor};">${prediction.state}</span>`
  },
  rowChange (row) {
    const original = this.original.find(e => e.race === row.race)
    return row.demExpenditures === original.demExpenditures && row.gopExpenditures === original.gopExpenditures
  },
  updateRowsPerPage () {
    if (this.mobilePhone) {
      this.pagination.rowsPerPage = 3
    } else if (!this.mobilePhone) {
      this.pagination.rowsPerPage = 435
    }
  },
  searchFiltering: debounce(function (search) {
    this.search = search
  }, 300),
  tableScroll (event) {
    const edit = document.getElementById('edit-input')
    if (edit) {
      const table = document.getElementById('expenditures-table')
      const { top, left } = edit.target.getBoundingClientRect()
      const newTop = top + window.scrollY
      if (!event && newTop + edit.offsetHeight > table.offsetTop + table.offsetHeight) {
        table.scrollTop = table.scrollTop + edit.offsetHeight
      } else if (newTop > table.offsetTop && newTop + edit.offsetHeight < table.offsetTop + table.offsetHeight) {
        edit.style.top = `${newTop}px`
        edit.style.left = `${left + window.scrollX}px`
      } else {
        edit.blur()
      }
    }
  }
}

const watch = {
  mobile () {
    if (this.mobile) {
      this.height = 'fit-content'
    } else {
      this.height = '400px'
    }
  },
  mobilePhone () {
    this.updateRowsPerPage()
  },
  tags () {
    if (this.tags.length > 0) this.search = ''
  }
}

const components = {
  Scoreboard,
  ScoreboardMobile,
  TagSelector
}

export default {
  name,
  mixins,
  props,
  computed,
  methods,
  watch,
  components,
  metaInfo: {
    title: 'Expenditure Calculator'
  },
  data () {
    return {
      loading: false,
      height: '400px',
      pagination: {
        sortBy: 'race',
        rowsPerPage: null,
        totalItems: 435
      },
      headers: [
        { text: 'Race', value: 'race', sortable: true, align: 'start' },
        { text: 'Prediction', value: 'finalProbability', sortable: true, align: 'start' },
        { text: 'Actions', value: 'action', sortable: false, align: 'start' },
        { text: 'Old', value: 'originalProbability', sortable: true, align: 'start' },
        { text: 'New', value: 'finalProbability', sortable: true, align: 'start' },
        { text: 'Shift', value: 'probabilityDelta.value', sortable: true, align: 'start' },
        { text: 'GOP', value: 'gopExpenditures', sortable: true, align: 'start', party: 'republican' },
        { text: 'GOP +/-', value: 'gopChange.value', sortable: true, align: 'start' },
        { text: 'Dem', value: 'demExpenditures', sortable: true, align: 'start', party: 'democratic' },
        { text: 'Dem +/-', value: 'demChange.value', sortable: true, align: 'start' }
      ],
      search: '',
      rowSelection: 'all',
      tags: ['Toss Up'],
      showSearch: false,
      showSettings: true
    }
  },
  beforeMount () {
    this.updateRowsPerPage()
    if (this.mobilePhone) {
      this.height = 'fit-content'
    } else {
      this.height = '400px'
    }
  },
  mounted () {
    const dataTable = document.querySelector('#expenditures-table')
    dataTable.addEventListener('scroll', this.tableScroll)
  }
}
</script>

<style>
.ie-search-bar {
  padding-top: 0px;
}

.ie-search-bar label {
  top: 2px !important;
}

#expenditures-table .table__overflow .datatable thead {
  background-color: #f5f5f5;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

#expenditures-table .table__overflow .datatable tbody td {
  padding: 10px;
}

#expenditures-table .datatable .datatable__actions .datatable__actions__select {
  display: none;
}

.group-col-header {
  font-weight: 400;
  letter-spacing: 0.05em;
}

#ie-table-settings .toolbar__content {
  height: 30px !important;
}

.ie-table-row:hover {
  background-color :red;
}

.race-state {
  text-align: center;
  font-size: 0.85em;
  font-weight: 500;
  margin: 0 3px;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 50px;
}

#ie-table-footer {
  margin: 10px 0;
  height: fit-content;
  background-color: #FAFAFA;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

.dialog__content .dialog__content__active {
  z-index: 3000 !important;
}

@media only screen and (max-width: 420px) {
  .ie-table-row {
    height: 80px;
  }

  ::-webkit-scrollbar {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/calculator/ExpendituresTable.vue